import React, { useState, useEffect } from 'react';
import { Batiment } from '../../../../shared/types/game';
import { getApiBaseUrl } from '../../../../core/config/api';

interface BuildingCTATabProps {
  ctaBuilding: Batiment;
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
}

// Obtenir le chemin de l'icône PNG selon le type de bâtiment
const getBuildingIconPath = (buildingType: string): string => {
  switch (buildingType) {
    case 'pompier':
      return '/images/feu.png';
    case 'hopital':
      return '/images/crois.png';
    case 'fdo':
      return '/images/forces-de-lordre.png';
    case 'prison':
      return '/images/prison.png';
    case 'cta':
      return '/images/antenne-cta.png';
    default:
      return '/images/icon-bat.png';
  }
};

// Configuration des couleurs par type de bâtiment
const getBuildingIconConfig = (buildingType: string): { backgroundColor: string; iconColor: string } => {
  switch (buildingType) {
    case 'pompier':
      return { backgroundColor: '#ef4444', iconColor: '#ffffff' };
    case 'hopital':
      return { backgroundColor: '#ffffff', iconColor: '#ffffff' };
    case 'fdo':
      return { backgroundColor: '#1e40af', iconColor: '#ffffff' };
    case 'prison':
      return { backgroundColor: '#ffffff', iconColor: '#ffffff' };
    case 'cta':
      return { backgroundColor: '#eab308', iconColor: '#ffffff' };
    default:
      return { backgroundColor: '#10b981', iconColor: '#ffffff' };
  }
};

const BuildingCTATab: React.FC<BuildingCTATabProps> = ({ ctaBuilding, onBuildingUpdate }) => {
  const [allBuildings, setAllBuildings] = useState<Batiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState<number | null>(null);
  const [searchLinked, setSearchLinked] = useState('');
  const [searchAvailable, setSearchAvailable] = useState('');

  // Charger tous les bâtiments de l'utilisateur
  useEffect(() => {
    const loadBuildings = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${getApiBaseUrl(true)}/buildings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            const buildings = Array.isArray(data.data) ? data.data : [];
            // Exclure le CTA lui-même et ne garder que les bâtiments actifs
            const otherBuildings = buildings.filter(
              (b: Batiment) => b.id !== ctaBuilding.id && b.type !== 'cta' && b.actif
            );
            setAllBuildings(otherBuildings);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des bâtiments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBuildings();
  }, [ctaBuilding.id]);

  // Bâtiments liés au CTA
  const linkedBuildings = allBuildings.filter(
    (b) => b.cta_id != null && Number(b.cta_id) === Number(ctaBuilding.id)
  );
  const unlinkedBuildings = allBuildings.filter(
    (b) => b.cta_id == null || Number(b.cta_id) !== Number(ctaBuilding.id)
  );

  // Filtrer les bâtiments liés selon la recherche
  const filteredLinkedBuildings = linkedBuildings.filter(building =>
    building.nom.toLowerCase().includes(searchLinked.toLowerCase().trim()) ||
    building.type.toLowerCase().includes(searchLinked.toLowerCase().trim())
  );

  // Filtrer les bâtiments disponibles selon la recherche
  const filteredAvailableBuildings = unlinkedBuildings.filter(building =>
    building.nom.toLowerCase().includes(searchAvailable.toLowerCase().trim()) ||
    building.type.toLowerCase().includes(searchAvailable.toLowerCase().trim())
  );

  const handleLinkBuilding = async (buildingId: number) => {
    setLinking(buildingId);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${getApiBaseUrl(true)}/buildings/${buildingId}/link-cta`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cta_id: ctaBuilding.id })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Recharger les bâtiments
          const loadBuildings = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await fetch(`${getApiBaseUrl(true)}/buildings`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data) {
                const buildings = Array.isArray(data.data) ? data.data : [];
                const otherBuildings = buildings.filter(
                  (b: Batiment) => b.id !== ctaBuilding.id && b.type !== 'cta' && b.actif
                );
                setAllBuildings(otherBuildings);
              }
            }
          };
          await loadBuildings();
        }
      }
    } catch (error) {
      console.error('Erreur lors de la liaison du bâtiment:', error);
    } finally {
      setLinking(null);
    }
  };

  const handleUnlinkBuilding = async (buildingId: number) => {
    setLinking(buildingId);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${getApiBaseUrl(true)}/buildings/${buildingId}/unlink-cta`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Recharger les bâtiments
          const loadBuildings = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await fetch(`${getApiBaseUrl(true)}/buildings`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data) {
                const buildings = Array.isArray(data.data) ? data.data : [];
                const otherBuildings = buildings.filter(
                  (b: Batiment) => b.id !== ctaBuilding.id && b.type !== 'cta' && b.actif
                );
                setAllBuildings(otherBuildings);
              }
            }
          };
          await loadBuildings();
        }
      }
    } catch (error) {
      console.error('Erreur lors de la déliaison du bâtiment:', error);
    } finally {
      setLinking(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-zinc-400">Chargement des bâtiments...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-blue-900/30 border border-sky-500/50 rounded-lg p-4">
        <p className="text-sm text-blue-200">
          <strong>Information :</strong> Seuls les sites opérationnels <strong>liés à ce CTA</strong> peuvent servir à
          générer des interventions. Chaque site (hors CTA) a un <strong>secteur circulaire de 5 km</strong> autour de sa
          position carte : l’intervention y est placée aléatoirement (ou manuellement dans ce cercle). Seuls les bâtiments
          actifs peuvent être liés. Sur la carte, ouvrez le menu interventions à droite : la prise d'appel doit être
          <strong> ouverte</strong> pour toute génération (en plus du CTA actif).
        </p>
      </div>

      {/* Bâtiments liés */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            Bâtiments liés ({linkedBuildings.length})
          </h3>
        </div>
        
        {/* Barre de recherche pour les bâtiments liés */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher dans les bâtiments liés..."
              value={searchLinked}
              onChange={(e) => setSearchLinked(e.target.value)}
              className="w-full px-4 py-2 pl-10 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <svg
              className="absolute left-3 top-2.5 w-5 h-5 text-zinc-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {linkedBuildings.length === 0 ? (
          <div className="text-center py-12 text-zinc-400 bg-zinc-800 rounded-lg border border-zinc-700">
            <p className="text-lg mb-2">Aucun bâtiment lié</p>
            <p className="text-sm">Liez des bâtiments ci-dessous pour qu'ils soient utilisés par le CTA</p>
          </div>
        ) : filteredLinkedBuildings.length === 0 ? (
          <div className="text-center py-12 text-zinc-400 bg-zinc-800 rounded-lg border border-zinc-700">
            <p className="text-lg mb-2">Aucun résultat trouvé</p>
            <p className="text-sm">Essayez de modifier votre recherche</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredLinkedBuildings.map((building) => (
              <div
                key={building.id}
                className="bg-zinc-800 border border-green-500/50 rounded-lg p-4 hover:border-green-500 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className="rounded-full flex items-center justify-center border-2 border-white shadow-md flex-shrink-0"
                      style={{
                        backgroundColor: getBuildingIconConfig(building.type).backgroundColor,
                        width: '40px',
                        height: '40px'
                      }}
                    >
                      <img
                        src={getBuildingIconPath(building.type)}
                        alt={building.type}
                        className="w-8 h-8"
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white mb-1">{building.nom}</h4>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">
                          Lié
                        </span>
                        <span className="px-2 py-1 rounded text-xs bg-sky-500/20 text-sky-400">
                          Niveau {building.niveau}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleUnlinkBuilding(building.id)}
                    disabled={linking === building.id}
                    className={`ml-4 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      linking === building.id
                        ? 'bg-zinc-600 text-zinc-400 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {linking === building.id ? '...' : 'Délier'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bâtiments disponibles */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            Bâtiments disponibles ({unlinkedBuildings.length})
          </h3>
        </div>

        {/* Barre de recherche pour les bâtiments disponibles */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher des bâtiments à lier..."
              value={searchAvailable}
              onChange={(e) => setSearchAvailable(e.target.value)}
              className="w-full px-4 py-2 pl-10 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
            />
            <svg
              className="absolute left-3 top-2.5 w-5 h-5 text-zinc-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {unlinkedBuildings.length === 0 ? (
          <div className="text-center py-12 text-zinc-400 bg-zinc-800 rounded-lg border border-zinc-700">
            <p className="text-lg mb-2">Aucun bâtiment disponible</p>
            <p className="text-sm">Tous vos bâtiments actifs sont déjà liés à ce CTA</p>
          </div>
        ) : filteredAvailableBuildings.length === 0 ? (
          <div className="text-center py-12 text-zinc-400 bg-zinc-800 rounded-lg border border-zinc-700">
            <p className="text-lg mb-2">Aucun résultat trouvé</p>
            <p className="text-sm">Essayez de modifier votre recherche</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredAvailableBuildings.map((building) => (
              <div
                key={building.id}
                className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 hover:border-sky-500 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className="rounded-full flex items-center justify-center border-2 border-white shadow-md flex-shrink-0"
                      style={{
                        backgroundColor: getBuildingIconConfig(building.type).backgroundColor,
                        width: '40px',
                        height: '40px'
                      }}
                    >
                      <img
                        src={getBuildingIconPath(building.type)}
                        alt={building.type}
                        className="w-8 h-8"
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white mb-1">{building.nom}</h4>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 rounded text-xs bg-zinc-700 text-zinc-400">
                          Non lié
                        </span>
                        <span className="px-2 py-1 rounded text-xs bg-sky-500/20 text-sky-400">
                          Niveau {building.niveau}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleLinkBuilding(building.id)}
                    disabled={linking === building.id}
                    className={`ml-4 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      linking === building.id
                        ? 'bg-zinc-600 text-zinc-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {linking === building.id ? '...' : 'Lier'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuildingCTATab;


